import { useCallback, useMemo } from "react";
import { ampli } from "../../../../ampli";
import { HighlightedSpan } from "../../../../utils/styled";
import { DEFAULT_TOAST, FEATURE_FLAG_KEYS } from "../../constants";
import { useUserContext } from "../../../../contexts/UserContext";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import {
  EntryAction,
  RowData,
  RowDataValueType,
  SetupTableProps,
} from "../../../../types";
import {
  removeMetaDataFieldsFromRowData,
  getSchemaTitles,
  getFieldsWithUITitles,
  getTranslatedColDefs,
  isSetupRecordActionsRestricted,
  evaluateSchemaContext,
  getBaseGridFields,
  getHeaderLabel,
  resolveFieldDisplayValue,
  formatFieldValueToString,
} from "../../helpers";
import {
  clearRowDataErrorsForNonClonedRecords,
  validateAllFields,
} from "../../validations";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import { useTranslation } from "react-i18next";
import SetupModeRenderer from "./SetupModeRenderer";
import { useTableHeaderProps } from "./UseTableHeaderProps";
import { useTableSearch } from "./UseTableSearch";
import { resolveCustomFieldRender } from "../custom-renderers/custom-fields/setupGridFieldRenderers";

export default function SetupTable({
  rowDataReducerState,
  dispatch,
  effectiveGridFields,
}: Readonly<SetupTableProps>): JSX.Element {
  const {
    setIsRecordOpen,
    setIsSideSheetOpen,
    isRecordDeleteDialogOpen,
    setIsRecordDeleteDialogOpen,
    setLandingPageToast,
    setRecordPageToast,
    isExportDialogOpen,
    setIsExportDialogOpen,
    isFileUploadDialogOpen,
    setIsFileUploadDialogOpen,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    selectedDashboardTabSectionKey,
    activeTabIndex,
    isRecordOpen,
    chipFilterValue,
    activeAccountsReceivableRecordMigrationInfo,
    setActiveAccountsReceivableRecordMigrationInfo,
  } = useUIStateContext();

  const {
    permissions,
    tableTitleInfo,
    setTableTitleInfo,
    setTableTitleInfoForTab,
    selected,
    evaluatedResults,
    iamUserState,
  } = useUserContext();

  const { schema, rowDataList, columnDefs } = rowDataReducerState;

  const { setupMode } = evaluateSchemaContext({
    schema,
    activeTabIndex,
    selectedDashboardTabSectionKey,
    isRecordOpen,
  });

  const schemaTitles = getSchemaTitles(schema);
  const setupName = schema.title;
  const gridFields = useMemo(
    () => effectiveGridFields ?? getBaseGridFields(schema),
    [effectiveGridFields, schema],
  );

  const { t, i18n } = useTranslation(selected.setup.value);
  const languageSpecificColDefs = i18n.language?.startsWith("en")
    ? columnDefs
    : getTranslatedColDefs(schema, t);

  const isPrivlibFeatureEnabled = useFeatureFlag(
    FEATURE_FLAG_KEYS.ENABLE_PRIVLIB_SETUP_MANAGEMENT,
    false,
  );

  const isSetupActionsLocked =
    isSetupRecordActionsRestricted(setupName, selectedDashboardTabSectionKey) &&
    isPrivlibFeatureEnabled;
  const isEditDisabled = !permissions.setupPermission.hasManage;
  const isDeleteDisabled = !permissions.setupPermission.hasManage;

  const headerLabel = getHeaderLabel(selected, selectedDashboardTabSectionKey);

  const getFieldDisplayValue = useCallback(
    (fieldName: string, rowData: RowData) =>
      resolveFieldDisplayValue({
        fieldName,
        fieldValue: rowData[fieldName],
        rowData,
        setupName,
        schema,
        iamUserState,
        evaluatedResults,
      }),
    [setupName, schema, iamUserState, evaluatedResults],
  );

  const { dataToDisplay, handleSearchInputChange, handleSearchInputClear } =
    useTableSearch({
      searchValue,
      setupName,
      chipFilterValue,
      rowDataList,
      gridFields,
      getFieldDisplayValue,
      headerLabel,
      selected,
      selectedDashboardTabSectionKey,
      setSearchValue,
      setTableTitleInfo,
      setTableTitleInfoForTab,
    });

  const handleExportRecords = () => {
    setIsExportDialogOpen(true);
    ampli.downloadIconClick({ "Open Download Popup": true });
  };

  const trackEntryAction = (
    action: EntryAction,
    currentRowData: Record<string, unknown>,
  ) => {
    const currentValues = getFieldsWithUITitles(currentRowData, schemaTitles);
    ampli.entryAction({
      Action: action,
      "Action Triggered": true,
      Store: selected.store.value,
      Application: selected.department.value,
      Setup: selected.setup.value,
      ...currentValues,
    });

    if (
      activeAccountsReceivableRecordMigrationInfo?.gridTableToast?.message
        .length &&
      action !== "View Entry"
    ) {
      setActiveAccountsReceivableRecordMigrationInfo((prev) => ({
        ...prev,
        isMigrationDialogOpen: true,
      }));
      return;
    }

    if (action === "Delete Entry") {
      setIsRecordDeleteDialogOpen(true);
      return;
    }

    if (
      setupMode === "NORMAL_SIDE_SHEET_SETUP" ||
      setupMode === "INVENTORY_BUILDINGS_SETUP"
    ) {
      setIsSideSheetOpen(true);
      setRecordPageToast(DEFAULT_TOAST);
    } else if (!setupMode.includes("DASHBOARD")) {
      setIsRecordOpen(true);
    }
  };

  const handleView = (rowData: RowData) => {
    setLandingPageToast(DEFAULT_TOAST);
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: { rowData, isReadOnly: true },
    });
    trackEntryAction("View Entry", rowData);
  };

  const handleEdit = (rowData: RowData) => {
    setLandingPageToast(DEFAULT_TOAST);
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: {
        rowData,
        resetState: { rowData },
        isReadOnly: !permissions.setupPermission.hasManage,
      },
    });
    trackEntryAction("Edit Entry", rowData);
    clearRowDataErrorsForNonClonedRecords(dispatch, rowData);
  };

  const handleCopy = async (rowData: RowData) => {
    const updatedRowdata = removeMetaDataFieldsFromRowData(rowData);
    setLandingPageToast(DEFAULT_TOAST);
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: {
        rowData: updatedRowdata,
        resetState: { rowData: updatedRowdata },
        isReadOnly: false,
      },
    });

    const { hasErrors, updatedRowDataErrors } = validateAllFields({
      rowDataReducerState: {
        ...rowDataReducerState,
        rowData: updatedRowdata,
        rowDataList,
      },
      dispatch,
      isForDialogFields: undefined,
      selectedTabSectionKey: undefined,
      selectedDashboardTabSectionKey,
    });

    if (hasErrors) {
      dispatch({
        type: "UPDATE_ROW_DATA_REDUCER_STATE",
        payload: { rowDataErrors: updatedRowDataErrors },
      });
    }

    trackEntryAction("Copy New Entry", updatedRowdata);
  };

  const handleDelete = (rowData: RowData) => {
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: { rowData },
    });
    setLandingPageToast(DEFAULT_TOAST);
    trackEntryAction("Delete Entry", rowData);
  };

  const highlightText = (text: string) => {
    if (!searchValue) return text;

    const value = searchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${value})`, "gi");

    return String(text)
      .split(regex)
      .map((part, index) =>
        part.toLowerCase() === searchValue.toLowerCase() ? (
          <HighlightedSpan key={index}>{part}</HighlightedSpan>
        ) : (
          part
        ),
      );
  };

  const renderField = (
    fieldName: string,
    fieldValue: RowDataValueType,
    rowData: RowData,
  ) => {
    const customRender = resolveCustomFieldRender({
      fieldName,
      fieldValue,
      rowData,
      setupName,
      schema,
      iamUserState,
      evaluatedResults,
      highlightText,
    });

    if (customRender !== undefined) return customRender;

    const displayValue = getFieldDisplayValue(fieldName, rowData);
    const formattedValue = formatFieldValueToString(fieldName, displayValue);
    return highlightText(formattedValue);
  };

  const tableHeaderProps = useTableHeaderProps({
    tableTitleInfo,
    searchValue,
    hasNoRows: dataToDisplay.length === 0,
    handleExportRecords,
    handleSearchInputChange,
    handleSearchInputClear,
  });

  return (
    <SetupModeRenderer
      setupMode={setupMode}
      effectiveGridFields={effectiveGridFields}
      rowDataReducerState={rowDataReducerState}
      dispatch={dispatch}
      languageSpecificColDefs={languageSpecificColDefs}
      dataToDisplay={dataToDisplay}
      tableHeaderProps={tableHeaderProps}
      gridFields={gridFields}
      setupName={setupName}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      isEditDisabled={isEditDisabled}
      isDeleteDisabled={isDeleteDisabled}
      isSetupActionsLocked={isSetupActionsLocked}
      isRecordDeleteDialogOpen={isRecordDeleteDialogOpen}
      isExportDialogOpen={isExportDialogOpen}
      isFileUploadDialogOpen={isFileUploadDialogOpen}
      setIsFileUploadDialogOpen={setIsFileUploadDialogOpen}
      isARMigrationDialogOpen={
        activeAccountsReceivableRecordMigrationInfo.isMigrationDialogOpen
      }
      handleView={handleView}
      handleEdit={handleEdit}
      handleCopy={handleCopy}
      handleDelete={handleDelete}
      renderField={renderField}
      getFieldDisplayValue={getFieldDisplayValue}
      headerLabel={headerLabel}
    />
  );
}
